import { RiMore2Fill } from "react-icons/ri";
import { useMenu } from "./useMenu";

export interface RowMenuItem {
  label: string;
  icon?: JSX.Element;
  onSelect(): void;
  danger?: boolean;
  disabled?: boolean;
  /** Right-aligned mark, for an item that represents a current choice. */
  trailing?: JSX.Element;
}

/** The per-row overflow menu shared by folder and file rows. */
export default function TreeRowMenu({
  items,
  label,
  tabIndex,
  className = "",
  icon,
  triggerClassName = "h-7 w-7 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700",
}: {
  items: RowMenuItem[];
  label: string;
  /** Roving tabindex: only the focused row's controls are in the tab order. */
  tabIndex?: number;
  /** Extra classes on the wrapper — the panel opens from its right edge, so a caller in a
   *  wrapping flex row must be able to pin it right or the panel runs off the screen. */
  className?: string;
  /** Trigger glyph. Defaults to the overflow dots the tree rows use. */
  icon?: JSX.Element;
  /** Trigger box, so a toolbar can match the control it sits beside. */
  triggerClassName?: string;
}) {
  const { open, setOpen, close, trigger, menu, onMenuKeyDown } = useMenu();

  const usable = items.filter((i) => !i.disabled);
  if (usable.length === 0) return <span className="w-7 shrink-0" aria-hidden="true" />;

  return (
    <div className={`relative shrink-0 ${className}`}>
      <button
        ref={trigger}
        type="button"
        tabIndex={tabIndex}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className={`flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${triggerClassName}`}
      >
        {icon ?? <RiMore2Fill size={15} aria-hidden="true" />}
      </button>
      {open && (
        <div
          ref={menu as React.RefObject<HTMLDivElement>}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-full z-50 mt-1 w-52 rounded-lg border border-gray-200 bg-white p-1 shadow-[0_10px_15px_-3px_rgba(0,0,0,.1),0_4px_6px_-4px_rgba(0,0,0,.1)]"
        >
          {usable.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                item.onSelect();
                close();
              }}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs font-medium leading-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                item.danger ? "text-red-600 hover:bg-red-50" : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {item.icon}
              {item.label}
              {item.trailing && <span className="ml-auto">{item.trailing}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
